import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErr, fileUrl } from "@/lib/api";
import { useCustomer } from "@/lib/customerAuth";
import { BRANDS, FUELS, MACHINE_TYPES, CAPACITIES, SERVICE_TYPES } from "@/lib/data";
import { Upload, X, Check, ArrowLeft, ArrowRight, Wrench } from "lucide-react";

const STEPS = ["Your Details", "Forklift Details", "Problem", "Appointment"];

export default function BookService() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user, signIn, signOut } = useCustomer();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [f, setF] = useState({
    company_name: "", contact_name: "", phone: "", email: "", address: "", suburb: "", postcode: "", preferred_contact: "Phone",
    forklift_brand: "Linde", forklift_model: "", serial_number: "", year: "", hours: "",
    fuel_type: "LPG", machine_type: "Counterbalance", capacity: "2–2.5T",
    service_type: params.get("urgency") === "breakdown" ? "Breakdown / Repair" : "Scheduled Service",
    problem_description: "", operational_status: "Yes",
    urgency: params.get("urgency") === "breakdown" ? "Breakdown — Priority Requested" : "Normal Service",
    preferred_date: "", preferred_time: "Flexible", alternative_date: "",
    site_hours: "", site_induction: false, access_notes: "",
    ack: false,
  });

  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));

  const next = () => {
    if (step === 0 && (!f.company_name || !f.contact_name || !f.phone || !f.email || !f.address || !f.suburb || !f.postcode)) return toast.error("Please fill required fields");
    if (step === 2 && (!f.problem_description)) return toast.error("Please describe the problem");
    if (step === 3 && (!f.preferred_date || !f.ack)) return toast.error("Please select a date and acknowledge");
    if (step < 3) setStep(step + 1); else submit();
  };

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const fd = new FormData();
    Array.from(files).slice(0, 5).forEach(x => fd.append("files", x));
    try {
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploads(u => [...u, ...r.data.urls].slice(0, 5));
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/bookings", { ...f, file_urls: uploads });
      nav(`/booking-confirmation/${r.data.booking_reference}`);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); setSubmitting(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold">Service Booking</div>
      <h1 className="font-display text-4xl md:text-5xl uppercase mt-2">Book a forklift service</h1>
      <p className="mt-3 text-[#555]">Provide some information about your forklift and we'll arrange the appropriate service visit.</p>

      <div className="mt-6 ff-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid="book-signin-bar">
        {user ? (
          <>
            <div className="text-sm">Signed in as <strong>{user.email}</strong> — this booking will be saved to your account.</div>
            <div className="flex gap-2">
              <a href="/my-bookings" className="text-xs uppercase tracking-widest font-bold hover:text-[#D71920]">My Bookings</a>
              <button onClick={signOut} className="text-xs uppercase tracking-widest font-bold text-[#666] hover:text-[#D71920]">Sign out</button>
            </div>
          </>
        ) : (
          <>
            <div className="text-sm text-[#333]"><strong>Save progress with an account.</strong> Sign in to track this booking and re-book with one click next time.</div>
            <div className="flex flex-wrap gap-2">
              <Link to="/login?next=/book-service" className="ff-btn-dark" data-testid="book-signin-email">Sign in with Email</Link>
              <button onClick={signIn} className="text-xs uppercase tracking-widest font-bold px-4 py-3 border-2 border-[#111] hover:bg-[#111] hover:text-white transition" data-testid="book-signin-btn">Sign in with Google</button>
            </div>
          </>
        )}
      </div>

      <div className="mt-8 grid grid-cols-4 gap-2">
        {STEPS.map((s, i) => {
          let chipCls = "border-[#e5e5e5] bg-white";
          if (i === step) chipCls = "border-[#D71920] bg-[#D71920] text-white";
          else if (i < step) chipCls = "border-[#111] bg-[#111] text-white";
          return (
            <div key={s} className={`p-3 border ${chipCls}`}>
              <div className="text-[10px] uppercase tracking-widest opacity-80">Step {i + 1}</div>
              <div className="font-display uppercase text-sm md:text-base mt-1 flex items-center gap-1">{i < step && <Check className="w-3 h-3" />}{s}</div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 ff-card p-6 md:p-8">
        {step === 0 && (
          <div className="grid md:grid-cols-2 gap-4" data-testid="step-0">
            <div><label className="ff-label">Company Name*</label><input value={f.company_name} onChange={e => upd("company_name", e.target.value)} className="ff-input" data-testid="b-company" /></div>
            <div><label className="ff-label">Contact Name*</label><input value={f.contact_name} onChange={e => upd("contact_name", e.target.value)} className="ff-input" data-testid="b-contact" /></div>
            <div><label className="ff-label">Mobile Phone*</label><input value={f.phone} onChange={e => upd("phone", e.target.value)} className="ff-input" data-testid="b-phone" /></div>
            <div><label className="ff-label">Email Address*</label><input type="email" value={f.email} onChange={e => upd("email", e.target.value)} className="ff-input" data-testid="b-email" /></div>
            <div className="md:col-span-2"><label className="ff-label">Site Address*</label><input value={f.address} onChange={e => upd("address", e.target.value)} className="ff-input" data-testid="b-address" /></div>
            <div><label className="ff-label">Suburb*</label><input value={f.suburb} onChange={e => upd("suburb", e.target.value)} className="ff-input" data-testid="b-suburb" /></div>
            <div><label className="ff-label">Postcode*</label><input value={f.postcode} onChange={e => upd("postcode", e.target.value)} className="ff-input" data-testid="b-postcode" /></div>
            <div><label className="ff-label">Preferred Contact</label>
              <select value={f.preferred_contact} onChange={e => upd("preferred_contact", e.target.value)} className="ff-input"><option>Phone</option><option>Email</option><option>SMS</option></select></div>
          </div>
        )}
        {step === 1 && (
          <div className="grid md:grid-cols-2 gap-4" data-testid="step-1">
            <div><label className="ff-label">Forklift Make*</label>
              <select value={f.forklift_brand} onChange={e => upd("forklift_brand", e.target.value)} className="ff-input" data-testid="b-brand">{BRANDS.map(b => <option key={b}>{b}</option>)}<option>Other</option></select></div>
            <div><label className="ff-label">Model</label><input value={f.forklift_model} onChange={e => upd("forklift_model", e.target.value)} className="ff-input" data-testid="b-model" /></div>
            <div><label className="ff-label">Serial Number</label><input value={f.serial_number} onChange={e => upd("serial_number", e.target.value)} className="ff-input" /></div>
            <div><label className="ff-label">Year</label><input value={f.year} onChange={e => upd("year", e.target.value)} className="ff-input" /></div>
            <div><label className="ff-label">Operating Hours</label><input value={f.hours} onChange={e => upd("hours", e.target.value)} className="ff-input" /></div>
            <div><label className="ff-label">Fuel / Power*</label>
              <select value={f.fuel_type} onChange={e => upd("fuel_type", e.target.value)} className="ff-input" data-testid="b-fuel">{FUELS.map(x => <option key={x}>{x}</option>)}<option>Petrol</option><option>Other</option></select></div>
            <div><label className="ff-label">Machine Type*</label>
              <select value={f.machine_type} onChange={e => upd("machine_type", e.target.value)} className="ff-input">{MACHINE_TYPES.map(x => <option key={x}>{x}</option>)}</select></div>
            <div><label className="ff-label">Capacity</label>
              <select value={f.capacity} onChange={e => upd("capacity", e.target.value)} className="ff-input">{CAPACITIES.map(x => <option key={x}>{x}</option>)}</select></div>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4" data-testid="step-2">
            <div><label className="ff-label">Main service type*</label>
              <select value={f.service_type} onChange={e => upd("service_type", e.target.value)} className="ff-input" data-testid="b-service-type">{SERVICE_TYPES.map(x => <option key={x}>{x}</option>)}</select></div>
            <div><label className="ff-label">Problem description*</label>
              <textarea rows={5} value={f.problem_description} onChange={e => upd("problem_description", e.target.value)} placeholder="Please describe the problem, when it started, any warning lights, unusual noises, leaks or other symptoms." className="ff-input" data-testid="b-problem" /></div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="ff-label">Is the forklift currently operational?</label>
                <select value={f.operational_status} onChange={e => upd("operational_status", e.target.value)} className="ff-input"><option>Yes</option><option>Partially</option><option>No</option></select></div>
              <div><label className="ff-label">Urgency</label>
                <select value={f.urgency} onChange={e => upd("urgency", e.target.value)} className="ff-input" data-testid="b-urgency"><option>Normal Service</option><option>Breakdown — Priority Requested</option><option>Machine Completely Inoperable</option></select></div>
            </div>
            <div>
              <label className="ff-label">Photos / videos (up to 5)</label>
              <label className="ff-card p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-[#D71920]" data-testid="b-upload">
                <Upload className="w-6 h-6 text-[#D71920]" />
                <span className="text-sm">Uploading photos of the forklift, data plate, warning codes or damaged components helps us prepare.</span>
                <input type="file" multiple accept="image/*,video/*,.pdf" className="hidden" onChange={e => handleUpload(e.target.files)} />
                {uploading && <span className="text-xs uppercase tracking-widest text-[#D71920]">Uploading…</span>}
              </label>
              {uploads.length > 0 && (
                <div className="mt-3 grid grid-cols-3 md:grid-cols-5 gap-2">
                  {uploads.map((u, i) => (
                    <div key={u} className="relative aspect-square border border-[#e5e5e5] bg-[#f4f4f4]">
                      <img src={fileUrl(u)} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setUploads(uploads.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-black/70 text-white p-0.5"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-4" data-testid="step-3">
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="ff-label">Preferred Date*</label><input type="date" value={f.preferred_date} onChange={e => upd("preferred_date", e.target.value)} className="ff-input" data-testid="b-date" /></div>
              <div><label className="ff-label">Preferred Time</label>
                <select value={f.preferred_time} onChange={e => upd("preferred_time", e.target.value)} className="ff-input"><option>Morning</option><option>Midday</option><option>Afternoon</option><option>Flexible</option></select></div>
              <div><label className="ff-label">Alternative Date</label><input type="date" value={f.alternative_date} onChange={e => upd("alternative_date", e.target.value)} className="ff-input" /></div>
              <div><label className="ff-label">Site operating hours</label><input value={f.site_hours} onChange={e => upd("site_hours", e.target.value)} className="ff-input" placeholder="e.g. Mon–Fri 7am–3pm" /></div>
              <div className="md:col-span-2"><label className="ff-label">Site access instructions</label><textarea rows={3} value={f.access_notes} onChange={e => upd("access_notes", e.target.value)} className="ff-input" /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.site_induction} onChange={e => upd("site_induction", e.target.checked)} /> Does site induction apply?</label>
            </div>
            <label className="flex items-start gap-3 border border-[#e5e5e5] p-4 bg-[#f4f4f4]">
              <input type="checkbox" checked={f.ack} onChange={e => upd("ack", e.target.checked)} className="mt-1" data-testid="b-ack" />
              <span className="text-sm">I understand that this is a service request and the appointment is not confirmed until Forkfix accepts the booking.</span>
            </label>
          </div>
        )}

        <div className="mt-8 flex justify-between">
          <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="ff-btn-ghost disabled:opacity-40" data-testid="b-prev">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button onClick={next} disabled={submitting} className="ff-btn-primary disabled:opacity-60" data-testid="b-next">
            {(() => {
              if (step < 3) return <>Continue <ArrowRight className="w-4 h-4" /></>;
              if (submitting) return "Sending…";
              return <><Wrench className="w-4 h-4" /> Request Service Booking</>;
            })()}
          </button>
        </div>
      </div>
    </div>
  );
}
